import { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Dropdown } from 'react-bootstrap';
import {
  Plus,
  X,
  Package,
  User,
  Truck,
  ClipboardList,
  Pencil,
  Trash2,
  Check,
  MessageCircle,
  AlertTriangle,
  Info,
  Printer,
  Bot,
} from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { OrderPdfPreview } from './OrderPdfPreview';
import { useNavigate } from 'react-router-dom';
import type {
  OrderSummary,
  LineItem,
  InboundRequest,
  OpsComment,
  InboundRequestItem,
  OrderAddress,
} from '../../../../infrastructure/OMSClient';
import { ShippingProviderType, ShippingStatus } from '../../../../infrastructure/OMSClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { getStatusBgColor, getStatusTextColor } from '../utils/statusColor';
import { MoneyInput, MoneyDisplay } from './MoneyInput';
import { useAuth } from '../../../../providers/AuthProvider';
import { DELIVERY_COMPANIES, getDeliveryCompanyName } from '../utils/deliveryCompany';
import { getStatusDescription, getStatusText } from '../utils/statusTextMapping';
import { ROUTES } from '../../../../routes/routes';
import { WooCommerceOrderStatus } from '../../../../models/ProductTypes';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import { useShipping } from '../../../../apps/hooks/shipping/useShipping';
import { toast } from 'react-hot-toast';
import { InboundStatus } from '../../../../models/InboundStatus';
import { useStock, OutboundStatus } from '../../../../apps/hooks/stock/useStock';
import type { DetailedStockStatus } from '../../../../infrastructure/StockClient';
import { skinSeoulProductsClient } from '../../../../infrastructure/SkinSeoulProductsClient';
import { getConfig } from '../../../../config';
import { OrderLabelPrintButton } from '../components/OrderLabelPrintButton';
import { appendPackingVerificationPages } from '../utils/parxlPackingVerificationPdf';

interface OrderDetailProps {
  order: OrderSummary;
  onUpdateLineItem: (lineItem: LineItem) => void;
  onRequestInbound: (request: Omit<InboundRequest, 'auditEntry'>) => void;
  onUpdateComments: (opsComments: OpsComment[]) => void;
  onUpdatePic?: (pic: string) => void;
  onUpdateBillingAddress?: (billingAddress: OrderAddress) => void;
  onUpdateShippingAddress?: (shippingAddress: OrderAddress) => void;
  onGetStockStatus: (skus: string[]) => void;
  stockStatusBySku: Record<string, number>;
  onRetryDeleoOrder: () => void;
  onResetDeleoOrder?: () => void;
  onCancelInbound: (inboundId: string) => void;
  onRefreshOrder?: () => void;
}

const SOURCING_ROUTES = [
  { value: 'Coupang', label: 'Coupang' },
  { value: 'Naver', label: 'Naver' },
  { value: 'Other retail', label: 'Other retail' },
  { value: 'Existing inventory', label: 'Existing inventory' },
] as const;

const getAdminBaseUrl = () => {
  const config = getConfig();
  return config.adminUrl;
};

export function OrderDetail({
  order,
  onUpdateLineItem: onUpdateLineItem,
  onRequestInbound,
  onUpdateComments,
  onUpdatePic,
  onUpdateBillingAddress,
  onUpdateShippingAddress,
  onGetStockStatus,
  stockStatusBySku,
  onRetryDeleoOrder,
  onResetDeleoOrder,
  onCancelInbound,
  onRefreshOrder,
}: OrderDetailProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const oms = useOMS();
  const stock = useStock();
  const { downloadParxlLabels, isDownloadingLabels } = useShipping();
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [processingOutbound, setProcessingOutbound] = useState(false);
  const [isAddingLineItem, setIsAddingLineItem] = useState(false);
  const [newLineItem, setNewLineItem] = useState<Partial<LineItem>>({});
  const [editingItem, setEditingItem] = useState<{ item: LineItem; index: number } | null>(null);
  const [itemToDelete, setItemToDelete] = useState<LineItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [showInboundConfirm, setShowInboundConfirm] = useState(false);
  const [trackingList, setTrackingList] = useState<InboundRequestItem[]>([]);
  const [opsComments, setOpsComments] = useState<OpsComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [inboundToCancel, setInboundToCancel] = useState<string | null>(null);
  const [detailedStockStatus, setDetailedStockStatus] = useState<Record<string, DetailedStockStatus>>({});
  const [loadingStockStatus, setLoadingStockStatus] = useState(false);
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  const [productNamesKor, setProductNamesKor] = useState<Record<string, string>>({});
  const [editingPic, setEditingPic] = useState(false);
  const [picValue, setPicValue] = useState(order.pic || '');
  const [editingBillingAddress, setEditingBillingAddress] = useState(false);
  const [editingShippingAddress, setEditingShippingAddress] = useState(false);
  const [showResetDeleoModal, setShowResetDeleoModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const [billingAddress, setBillingAddress] = useState<OrderAddress>(
    order.billingAddress || {
      firstName: order.customer.name.split(' ')[0] || '',
      lastName: order.customer.name.split(' ').slice(1).join(' ') || '',
      address1: '',
      city: '',
      state: '',
      postcode: '',
      country: order.countryCode || '',
      email: order.customer.email || '',
      phone: order.customer.phone || '',
    },
  );
  const [shippingAddress, setShippingAddress] = useState<OrderAddress>(
    order.shippingAddress || {
      firstName: order.consignee.name.split(' ')[0] || '',
      lastName: order.consignee.name.split(' ').slice(1).join(' ') || '',
      address1: order.consignee.address || '',
      city: order.consignee.city || '',
      state: order.consignee.state || '',
      postcode: order.consignee.zipcode || '',
      country: order.consignee.countryCode || '',
      phone: '',
    },
  );

  useEffect(() => {
    document.title = `${order.orderId} - Order Detail`;
    return () => {
      document.title = '';
    };
  }, [order.orderId]);

  useEffect(() => {
    if (order.billingAddress) {
      setBillingAddress(order.billingAddress);
    }
  }, [order.billingAddress]);

  useEffect(() => {
    if (order.shippingAddress) {
      setShippingAddress(order.shippingAddress);
    }
  }, [order.shippingAddress]);

  useEffect(() => {
    if (order.lineItems.length > 0) {
      onGetStockStatus(order.lineItems.map((item) => item.sku));
    }
  }, [order.lineItems]);

  // Fetch detailed stock status
  useEffect(() => {
    const fetchDetailedStockStatus = async () => {
      if (order.lineItems.length > 0) {
        setLoadingStockStatus(true);
        try {
          await refreshDetailedStockStatus();
        } catch (error) {
          console.error('Error fetching detailed stock status:', error);
          toast.error('Failed to fetch detailed stock status');
        } finally {
          setLoadingStockStatus(false);
        }
      }
    };

    fetchDetailedStockStatus();
  }, [order.lineItems]);

  // Fetch product images and Korean names by SKU
  useEffect(() => {
    const fetchProductData = async () => {
      if (order.lineItems.length === 0) {
        return;
      }

      try {
        const skus = order.lineItems.map((item) => item.sku);
        const products = await skinSeoulProductsClient.getProductsBySku(skus);

        const imagesMap: Record<string, string> = {};
        const namesKorMap: Record<string, string> = {};

        products.forEach((product) => {
          const firstImageUrl = product.productImageUrls?.[0];
          if (firstImageUrl) {
            imagesMap[product.sku] = firstImageUrl;
          }

          const nameKor = product.attributes?.ss_name_kor;
          if (nameKor) {
            namesKorMap[product.sku] = nameKor;
          }
        });

        setProductImages(imagesMap);
        setProductNamesKor(namesKorMap);
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    };

    fetchProductData();
  }, [order.lineItems]);

  // Fetch stock status when new item is added
  const handleAddLineItem = async () => {
    if (newLineItem.sku && newLineItem.quantity) {
      await onUpdateLineItem({
        ...newLineItem,
      } as LineItem);

      setNewLineItem({});
      setIsAddingLineItem(false);
    }
  };

  useEffect(() => {
    if (order.opsComments) {
      setOpsComments(order.opsComments);
    }
  }, [order.opsComments]);

  useEffect(() => {
    setPicValue(order.pic || '');
  }, [order.pic]);

  const handleEditItem = (item: LineItem, index: number) => {
    setEditingItem({ item, index });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) {
      return;
    }

    try {
      await onUpdateLineItem(editingItem.item);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving edit:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleEditChange = (field: keyof LineItem, value: any) => {
    if (editingItem) {
      setEditingItem({
        ...editingItem,
        item: {
          ...editingItem.item,
          [field]: value,
        },
      });
    }
  };

  const handleDeleteItem = (item: LineItem) => {
    setItemToDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      console.log('Delete item:', itemToDelete);

      itemToDelete.toDelete = true;
      try {
        await onUpdateLineItem(itemToDelete);
        setItemToDelete(null);
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select items that have CP Order Number
      const validIndices = order.lineItems
        .map((item, index) => (item.cpOrderNumber ? index : -1))
        .filter((index) => index !== -1);
      setSelectedItems(new Set(validIndices));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleCheckboxChange = (index: number) => {
    // Only allow toggling if the item has CP Order Number
    if (!order.lineItems[index]?.cpOrderNumber) {
      return;
    }
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(index)) {
      newSelectedItems.delete(index);
    } else {
      newSelectedItems.add(index);
    }
    setSelectedItems(newSelectedItems);
  };

  const handleRequestInbound = () => {
    // Group items by cpOrderNumber
    const selectedLineItems = Array.from(selectedItems).map((index) => order.lineItems[index]);
    const groupedByTracking = selectedLineItems.reduce(
      (acc, item) => {
        if (!item!.cpOrderNumber) {
          console.log('No CP Order Number for item:', item!.sku);
          return acc;
        }

        const trackingNo = item!.cpOrderNumber || 'unknown';
        if (!acc[trackingNo]) {
          acc[trackingNo] = {
            items: [],
            deliveryCompanyCode: item!.deliveryCompanyCd || '',
          };
        }
        acc[trackingNo].items.push({
          code: item!.sku!,
          quantity: item!.quantity!,
        });
        return acc;
      },
      {} as Record<string, { items: Array<{ code: string; quantity: number }>; deliveryCompanyCode: string }>,
    );

    // Convert to trackingList format
    const trackingList = Object.entries(groupedByTracking).map(([trackingNo, group]) => ({
      trackingNo,
      itemList: group.items,
      deliveryCompanyCode: group.deliveryCompanyCode,
    }));
    setTrackingList(trackingList);
    setShowInboundConfirm(true);
  };

  const handleConfirmInbound = () => {
    onRequestInbound({
      trackingList: trackingList,
    });
    setShowInboundConfirm(false);
  };

  const handleStatusUpdate = async (newStatus: WooCommerceOrderStatus) => {
    if (!user) {
      toast.error('User authentication required');
      return;
    }

    setUpdatingStatus(true);

    try {
      const updatedOrder = await oms.updateOrderStatus(
        order.orderId,
        newStatus,
        user.name || user.email || 'unknown-user',
      );

      if (updatedOrder) {
        // Status was successfully updated, refresh order data
        if (onRefreshOrder) {
          onRefreshOrder();
        }
      } else {
        toast.error('Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConfirmCancelInbound = () => {
    if (inboundToCancel) {
      onCancelInbound(inboundToCancel);
      setInboundToCancel(null);
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim() || !user) {
      return;
    }

    // server generates timestamp and id
    const opsComment: OpsComment = {
      operator: user.name,
      message: newComment.trim(),
    };

    onUpdateComments([opsComment]);
    setOpsComments([...opsComments, opsComment]);
    setNewComment('');
  };

  const canUseExistingInventory = (sku: string, quantity: number) => {
    return quantity && (stockStatusBySku[sku] ?? 0) >= quantity;
  };

  // Check if we should show "Use existing inventory" button
  const shouldShowUseInventoryButton = (item: LineItem) => {
    const stockStatus = detailedStockStatus[item.sku];
    if (!stockStatus) {
      return false;
    }

    const remainingQuantity = stockStatus.totalQuantity - stockStatus.totalReservedQuantity;

    return (
      order.status === 'processing' &&
      remainingQuantity > 0 &&
      item.inboundStatus !== InboundStatus.INBOUNDED &&
      remainingQuantity >= item.quantity
    );
  };

  // Get remaining quantity for display
  const getRemainingQuantity = (sku: string): number => {
    const stockStatus = detailedStockStatus[sku];
    if (!stockStatus) {
      return 0;
    }
    return stockStatus.totalQuantity - stockStatus.totalReservedQuantity;
  };

  // Helper function to refresh detailed stock status
  const refreshDetailedStockStatus = async () => {
    const skus = order.lineItems.map((item) => item.sku);

    // First get product info to map SKU -> productId
    const products = await stock.getProductsBySkus(skus);

    // Then get stock status using SKUs (API will convert to productIds internally)
    const response = await stock.getStockStatus(skus);

    // Convert productId-based response to SKU-based for easier access
    const skuBasedStockStatus: Record<string, DetailedStockStatus> = {};
    Object.entries(response.productStocks).forEach(([productId, stockStatus]) => {
      // Find the SKU that corresponds to this productId
      const sku = products.find((p) => p.productId.toString() === productId)?.sku;
      if (sku) {
        skuBasedStockStatus[sku] = stockStatus;
      }
    });

    setDetailedStockStatus(skuBasedStockStatus);
  };

  // Handle use existing inventory button click
  const handleUseExistingInventory = async (item: LineItem) => {
    if (!user) {
      toast.error('User authentication required');
      return;
    }

    if (order.status !== 'processing') {
      toast.error('Can only use existing inventory for orders with "processing" status');
      return;
    }

    try {
      // Get productId from SKU
      const product = await stock.getProductBySku(item.sku);

      // Prepare outbound request
      const outboundRequest: any = {
        productId: product.productId,
        qty: item.quantity,
        effectiveAt: new Date().toISOString(),
        locationId: 'WH#00',
        status: OutboundStatus.PENDING,
        orderId: order.orderId,
        auditor: user.name || user.email || 'unknown-user',
      };

      // Include optional properties if they have values
      if (item.cpOrderNumber) {
        outboundRequest.trackingNumber = item.cpOrderNumber;
      }
      if (item.deliveryCompanyCd) {
        outboundRequest.carrier = item.deliveryCompanyCd;
      }

      const result = await stock.outboundStock(outboundRequest);

      if (result.success) {
        toast.success(`Successfully reserved ${item.quantity} units of ${item.sku} from existing inventory`);

        // Update the item's sourcing route to "Existing inventory" and set inbound status to PENDING
        const updatedItem = { ...item, sourcingRoute: 'Existing inventory', inboundStatus: InboundStatus.INBOUNDED };
        await onUpdateLineItem(updatedItem);

        // Refresh detailed stock status
        await refreshDetailedStockStatus();
      } else {
        toast.error(`Failed to reserve inventory: ${result.message}`);
      }
    } catch (error) {
      console.error('Error using existing inventory:', error);
      toast.error(`Failed to use existing inventory: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const canOutboundToDeleo = () => {
    return (
      order.status === WooCommerceOrderStatus.PROCESSING &&
      order.lineItems.length > 0 &&
      order.lineItems.every((item) => item.inboundStatus === InboundStatus.INBOUNDED)
    );
  };

  const handleOutboundToDeleo = async () => {
    if (!user) {
      toast.error('User authentication required');
      return;
    }

    if (order.status !== WooCommerceOrderStatus.PROCESSING) {
      toast.error('Can only outbound to Deleo for orders with "processing" status');
      return;
    }

    if (!canOutboundToDeleo()) {
      toast.error('All line items must be INBOUNDED to proceed with outbound');
      return;
    }

    setProcessingOutbound(true);

    try {
      const outboundPromises = order.lineItems.map(async (item) => {
        try {
          // Get productId from SKU
          const product = await stock.getProductBySku(item.sku);

          // Prepare outbound request
          const outboundRequest: any = {
            productId: product.productId,
            qty: item.quantity,
            effectiveAt: new Date().toISOString(),
            locationId: 'WH#00',
            status: OutboundStatus.CONFIRMED,
            orderId: order.orderId,
            auditor: user.name || user.email || 'unknown-user',
          };

          // Only include optional properties if they have values
          if (item.cpOrderNumber) {
            outboundRequest.trackingNumber = item.cpOrderNumber;
          }
          if (item.deliveryCompanyCd) {
            outboundRequest.carrier = item.deliveryCompanyCd;
          }

          return await stock.outboundStock(outboundRequest);
        } catch (error) {
          console.error(`Failed to outbound ${item.sku}:`, error);
          throw new Error(
            `Failed to outbound ${item.sku}: ${error instanceof Error ? error.message : 'Unknown error'}`,
          );
        }
      });

      const results = await Promise.all(outboundPromises);

      // Check if all outbound requests were successful
      const failedItems = results.filter((result) => !result.success);

      if (failedItems.length === 0) {
        toast.success(`Successfully outbounded ${order.lineItems.length} items to Deleo BaroShip`);

        // Update order status to SHIPPED
        try {
          await handleStatusUpdate(WooCommerceOrderStatus.PREPARE_SHIPMENT);
          toast.success('Order status updated to PREPARE_FOR_SHIPMENT');
        } catch (statusError) {
          console.error('Error updating order status to PREPARE_FOR_SHIPMENT:', statusError);
          toast.error('Outbound successful but failed to update order status to PREPARE_FOR_SHIPMENT');
        }

        // Refresh order data to reflect the changes
        if (onRefreshOrder) {
          onRefreshOrder();
        }
      } else {
        toast.error(`${failedItems.length} items failed to outbound. Check console for details.`);
      }
    } catch (error) {
      console.error('Error processing outbound:', error);
      toast.error(`Failed to outbound items: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingOutbound(false);
    }
  };

  // total amount is amt * quantity from all items
  const getDeleoTotalAmount = () => {
    return order.deleoData?.orderItemList?.reduce((acc, item) => acc + item.amt * item.quantity, 0) ?? 0;
  };

  const getWooLineItemsTotalAmount = () => {
    return order.lineItems?.reduce((acc, item) => acc + item.subtotal, 0) ?? 0;
  };

  const getInboundStatusBadgeClass = (status?: InboundStatus) => {
    switch (status) {
      case InboundStatus.INBOUNDED:
        return 'bg-success';
      case InboundStatus.PENDING:
      case undefined:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  };

  const getInboundStatusText = (status?: InboundStatus) => {
    return status || InboundStatus.PENDING;
  };

  const getCurrentShippingProvider = (): ShippingProviderType | null => {
    if (order.shippingData?.provider) return order.shippingData.provider;
    if (order.parxlData) return ShippingProviderType.PARXL;
    if (order.deleoData?.deleoOrderId) return ShippingProviderType.DELEO;
    return null;
  };

  const getShippingProviderBadge = () => {
    const provider = getCurrentShippingProvider();
    if (provider === ShippingProviderType.PARXL) {
      return { text: 'PARXL', bg: 'bg-info', color: 'text-white' };
    }
    if (provider === ShippingProviderType.DELEO) {
      return { text: 'DELEO', bg: 'bg-primary', color: 'text-white' };
    }
    return null;
  };

  const openPdfFromBytes = (pdfBytes: Uint8Array) => {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  const handleDownloadLabel = async () => {
    const provider = getCurrentShippingProvider();

    switch (provider) {
      case ShippingProviderType.PARXL: {
        const trackingId = order.parxlData?.plsShipmentReference || order.shippingData?.providerOrderId;
        if (!trackingId) {
          toast.error('No Parxl tracking ID found');
          return;
        }
        try {
          const response = await downloadParxlLabels({ trackingIDs: [trackingId] });
          if (response.success && response.labels && response.labels.length > 0 && response.labels[0]?.pdfData) {
            const byteCharacters = atob(response.labels[0].pdfData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const originalPdfBytes = new Uint8Array(byteNumbers);
            const modifiedPdfBytes = await appendPackingVerificationPages(originalPdfBytes, order);
            openPdfFromBytes(modifiedPdfBytes);
            toast.success('Label downloaded successfully');
          } else {
            toast.error('Failed to download label');
          }
        } catch (error) {
          toast.error(`Failed to download label: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        break;
      }
      case ShippingProviderType.DELEO:
      default:
        setShowPdfPreview(true);
        break;
    }
  };

  const getLabelButtonText = () => {
    const provider = getCurrentShippingProvider();
    switch (provider) {
      case ShippingProviderType.PARXL:
        return isDownloadingLabels ? 'Downloading...' : 'Download Label';
      case ShippingProviderType.DELEO:
      default:
        return 'View Label';
    }
  };

  const getShippingStatusBadgeClass = (status?: ShippingStatus) => {
    switch (status) {
      case ShippingStatus.DELIVERED:
        return 'bg-success';
      case ShippingStatus.IN_TRANSIT:
      case ShippingStatus.OUT_FOR_DELIVERY:
        return 'bg-info';
      case ShippingStatus.PICKED_UP:
        return 'bg-primary';
      case ShippingStatus.FAILED:
      case ShippingStatus.CANCELLED:
        return 'bg-danger';
      case ShippingStatus.RETURNED:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: 27.94cm 21.59cm landscape !important;
        margin: 0 !important;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        html, body {
          width: 27.94cm !important;
          height: 21.59cm !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        .modal-body,
        .modal-content,
        .modal {
          margin: 0 !important;
          padding: 0 !important;
          max-height: none !important;
          overflow: visible !important;
          border: none !important;
          box-shadow: none !important;
          width: 27.94cm !important;
          height: 21.59cm !important;
        }
        .print-area {
          width: 27.94cm !important;
          height: 21.59cm !important;
          max-width: 27.94cm !important;
          max-height: 21.59cm !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 60px !important;
          padding-left: 15% !important;
          padding-right: 15% !important;
          overflow: hidden !important;
        }
      }
    `,
  });

  return (
    <div className="space-y-2 mt-4">
      <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>Order # {order.orderId}</span>
        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
          <span
            className="font-medium"
            style={{
              color: getStatusTextColor(order.status),
              backgroundColor: getStatusBgColor(order.status),
              padding: '0.25rem 0.5rem',
              borderRadius: '0.25rem',
              display: 'inline-block',
              fontSize: '1.6rem',
            }}
          >
            {order.status &&
              order.status
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
          </span>
          {(() => {
            const badge = getShippingProviderBadge();
            return badge ? (
              <span
                className={`font-medium ${badge.bg} ${badge.color}`}
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  display: 'inline-block',
                  fontSize: '1rem',
                }}
              >
                {badge.text}
              </span>
            ) : null;
          })()}
          <Button
            variant="outline-info"
            size="sm"
            onClick={handleDownloadLabel}
            disabled={isDownloadingLabels}
            style={{ fontSize: '12px', padding: '4px 12px' }}
          >
            {getLabelButtonText()}
          </Button>
          <OrderLabelPrintButton order={order} />
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              disabled={updatingStatus}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              {updatingStatus ? 'Updating...' : 'Change Status'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.values(WooCommerceOrderStatus)
                .filter((status) => status !== WooCommerceOrderStatus.CANCELLED)
                .map((status) => (
                  <Dropdown.Item
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    active={order.status === status}
                    disabled={updatingStatus}
                  >
                    {status}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Table className="mt-2">
        <tbody>
          <tr>
            <td>
              <Card>
                <CardHeader className="py-2">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Order Information</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  <dl className="space-y-2">
                    <div key="order-date" className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">Order Date</dt>
                      <dd className="text-sm font-medium">{order.orderDate} SGT</dd>
                    </div>
                    <div key="order-created" className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">Order Created At</dt>
                      <dd className="text-sm font-medium">
                        {new Date(order.orderDateTime)
                          .toLocaleString('en-GB', { timeZone: 'Asia/Singapore', hour12: false })
                          .replace(',', '')}{' '}
                        SGT
                      </dd>
                    </div>
                    <div key="total-items" className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">Total Items</dt>
                      <dd className="text-sm font-medium">{order.lineItems.length}</dd>
                    </div>
                    <div key="total-discount" className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">Total Discount</dt>
                      <dd className="text-sm font-medium">
                        {order.currency} {order.discount?.toFixed(2)}
                      </dd>
                    </div>
                    <div key="total-amount" className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">Total Amount</dt>
                      <dd className="text-sm font-medium">
                        {order.currency} {order.totalAmount}
                      </dd>
                    </div>
                    <div key="pic" className="flex justify-between items-center">
                      <dt
                        className="text-sm text-muted-foreground"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        PIC
                        {onUpdatePic && !editingPic && (
                          <button
                            type="button"
                            onClick={() => {
                              setPicValue(order.pic || '');
                              setEditingPic(true);
                            }}
                            className="btn btn-link p-0 border-0"
                            style={{
                              color: '#6c757d',
                              textDecoration: 'none',
                              fontSize: '12px',
                              lineHeight: '1',
                              padding: '0',
                              minWidth: 'auto',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#0d6efd';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#6c757d';
                            }}
                          >
                            <Pencil className="h-2.5 w-2.5" style={{ margin: 0, verticalAlign: 'middle' }} />
                          </button>
                        )}
                      </dt>
                      <dd className="text-sm font-medium">
                        {editingPic ? (
                          <div className="flex items-center gap-2">
                            <Form.Control
                              type="text"
                              size="sm"
                              value={picValue}
                              onChange={(e) => setPicValue(e.target.value)}
                              style={{ minWidth: '150px' }}
                              autoFocus
                            />
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={async () => {
                                if (onUpdatePic) {
                                  await onUpdatePic(picValue);
                                }
                                setEditingPic(false);
                              }}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setPicValue(order.pic || '');
                                setEditingPic(false);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span>{order.pic || '-'}</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </td>

            {/* Billing Address Card */}
            <td className="p-0">
              <Card>
                <CardHeader className="py-2">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <CardTitle style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Billing Address
                      {onUpdateBillingAddress && (
                        <button
                          type="button"
                          onClick={() => setEditingBillingAddress(!editingBillingAddress)}
                          className="btn btn-link p-0 border-0"
                          style={{
                            color: '#6c757d',
                            textDecoration: 'none',
                            fontSize: '12px',
                            lineHeight: '1',
                            padding: '0',
                            minWidth: 'auto',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#0d6efd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#6c757d';
                          }}
                        >
                          <Pencil className="h-2.5 w-2.5" style={{ margin: 0, verticalAlign: 'middle' }} />
                        </button>
                      )}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {editingBillingAddress ? (
                    <div className="space-y-2">
                      <Form.Group>
                        <Form.Label className="text-sm">First Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.firstName}
                          onChange={(e) => setBillingAddress({ ...billingAddress, firstName: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Last Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.lastName}
                          onChange={(e) => setBillingAddress({ ...billingAddress, lastName: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Company</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.company || ''}
                          onChange={(e) => setBillingAddress({ ...billingAddress, company: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Email</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          value={billingAddress.email || ''}
                          onChange={(e) => setBillingAddress({ ...billingAddress, email: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Phone</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.phone}
                          onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Address</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.address1}
                          onChange={(e) => setBillingAddress({ ...billingAddress, address1: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Address 2</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.address2 || ''}
                          onChange={(e) => setBillingAddress({ ...billingAddress, address2: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">City</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.city}
                          onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">State</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.state}
                          onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Postcode</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.postcode}
                          onChange={(e) => setBillingAddress({ ...billingAddress, postcode: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Country</Form.Label>
                        <Form.Control
                          size="sm"
                          value={billingAddress.country}
                          onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                        />
                      </Form.Group>
                      <div className="flex gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={async () => {
                            if (onUpdateBillingAddress) {
                              await onUpdateBillingAddress(billingAddress);
                            }
                            setEditingBillingAddress(false);
                          }}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setBillingAddress({
                              firstName: order.customer.name.split(' ')[0] || '',
                              lastName: order.customer.name.split(' ').slice(1).join(' ') || '',
                              address1: '',
                              city: '',
                              state: '',
                              postcode: '',
                              country: order.countryCode || '',
                              email: order.customer.email || '',
                              phone: order.customer.phone || '',
                            });
                            setEditingBillingAddress(false);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <dl className="space-y-2">
                      <div key="billing-firstName" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">First Name</dt>
                        <dd className="text-sm font-medium">{billingAddress.firstName || '-'}</dd>
                      </div>
                      <div key="billing-lastName" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Last Name</dt>
                        <dd className="text-sm font-medium">{billingAddress.lastName || '-'}</dd>
                      </div>
                      {billingAddress.company && (
                        <div key="billing-company" className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Company</dt>
                          <dd className="text-sm font-medium">{billingAddress.company}</dd>
                        </div>
                      )}
                      <div key="billing-email" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Email</dt>
                        <dd className="text-sm font-medium">{billingAddress.email || order.customer.email || '-'}</dd>
                      </div>
                      <div key="billing-phone" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Phone</dt>
                        <dd className="text-sm font-medium">{billingAddress.phone || order.customer.phone || '-'}</dd>
                      </div>
                      <div key="billing-address1" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Address</dt>
                        <dd className="text-sm font-medium">{billingAddress.address1 || '-'}</dd>
                      </div>
                      {billingAddress.address2 && (
                        <div key="billing-address2" className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Address 2</dt>
                          <dd className="text-sm font-medium">{billingAddress.address2}</dd>
                        </div>
                      )}
                      <div key="billing-city" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">City</dt>
                        <dd className="text-sm font-medium">{billingAddress.city || '-'}</dd>
                      </div>
                      <div key="billing-state" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">State</dt>
                        <dd className="text-sm font-medium">{billingAddress.state || '-'}</dd>
                      </div>
                      <div key="billing-postcode" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Postcode</dt>
                        <dd className="text-sm font-medium">{billingAddress.postcode || '-'}</dd>
                      </div>
                      <div key="billing-country" className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Country</dt>
                        <dd className="text-sm font-medium">{billingAddress.country || order.countryCode || '-'}</dd>
                      </div>
                    </dl>
                  )}
                </CardContent>
              </Card>
            </td>

            {/* Shipping Address Card */}
            <td className="p-0">
              <Card>
                <CardHeader className="py-2">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-muted-foreground" />
                    <CardTitle style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Shipping Address
                      {onUpdateShippingAddress && (
                        <button
                          type="button"
                          onClick={() => setEditingShippingAddress(!editingShippingAddress)}
                          className="btn btn-link p-0 border-0"
                          style={{
                            color: '#6c757d',
                            textDecoration: 'none',
                            fontSize: '12px',
                            lineHeight: '1',
                            padding: '0',
                            minWidth: 'auto',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#0d6efd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#6c757d';
                          }}
                        >
                          <Pencil className="h-2.5 w-2.5" style={{ margin: 0, verticalAlign: 'middle' }} />
                        </button>
                      )}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  {editingShippingAddress ? (
                    <div className="space-y-2">
                      <Form.Group>
                        <Form.Label className="text-sm">First Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.firstName}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, firstName: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Last Name</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.lastName}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, lastName: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Company</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.company || ''}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, company: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Email</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          value={shippingAddress.email || ''}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Phone</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.phone}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Address</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.address1}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, address1: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Address 2</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.address2 || ''}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, address2: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">City</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.city}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">State</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.state}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Postcode</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.postcode}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, postcode: e.target.value })}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="text-sm">Country</Form.Label>
                        <Form.Control
                          size="sm"
                          value={shippingAddress.country}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                        />
                      </Form.Group>
                      <div className="flex gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={async () => {
                            if (onUpdateShippingAddress) {
                              await onUpdateShippingAddress(shippingAddress);
                            }
                            setEditingShippingAddress(false);
                          }}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setShippingAddress({
                              firstName: order.consignee.name.split(' ')[0] || '',
                              lastName: order.consignee.name.split(' ').slice(1).join(' ') || '',
                              address1: order.consignee.address || '',
                              city: order.consignee.city || '',
                              state: order.consignee.state || '',
                              postcode: order.consignee.zipcode || '',
                              country: order.consignee.countryCode || '',
                              phone: '',
                            });
                            setEditingShippingAddress(false);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">First Name</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.firstName || order.consignee.name.split(' ')[0] || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Last Name</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.lastName || order.consignee.name.split(' ').slice(1).join(' ') || '-'}
                        </dd>
                      </div>
                      {shippingAddress.company && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Company</dt>
                          <dd className="text-sm font-medium text-right">{shippingAddress.company}</dd>
                        </div>
                      )}
                      {shippingAddress.email && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Email</dt>
                          <dd className="text-sm font-medium text-right">{shippingAddress.email}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Phone</dt>
                        <dd className="text-sm font-medium text-right">{shippingAddress.phone || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Address</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.address1 || order.consignee.address || '-'}
                        </dd>
                      </div>
                      {shippingAddress.address2 && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Address 2</dt>
                          <dd className="text-sm font-medium text-right">{shippingAddress.address2}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">City</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.city || order.consignee.city || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">State</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.state || order.consignee.state || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Postcode</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.postcode || order.consignee.zipcode || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-muted-foreground">Country</dt>
                        <dd className="text-sm font-medium text-right">
                          {shippingAddress.country || order.consignee.countryCode || '-'}
                        </dd>
                      </div>
                      {order.shippingMessage && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-muted-foreground">Shipping Message</dt>
                          <dd className="text-sm font-medium text-right">{order.shippingMessage}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </CardContent>
              </Card>
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Comments Card */}
      <Table>
        <tbody>
          <tr>
            <td className="pr-4" style={{ width: '50%' }}>
              <Card>
                <CardHeader className="pr-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Operator Comments</CardTitle>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowComments(!showComments)}
                      className="mb-2"
                    >
                      {showComments ? 'Hide Comments' : 'Show Comments'}
                    </Button>
                  </div>
                </CardHeader>
                {showComments && (
                  <CardContent className="py-2">
                    <div className="space-y-2">
                      {opsComments && opsComments.length === 0 ? (
                        <div key="no-comments" className="text-muted-foreground py-4">
                          No comments yet
                        </div>
                      ) : (
                        opsComments.map((comment) => (
                          <div key={comment.id} className="border rounded p-2 mb-1">
                            <div
                              key={`comment-header-${comment.id}`}
                              className="flex items-center gap-2 border-b pb-2 mb-2"
                            >
                              <span className="text-sm text-muted-foreground">
                                {comment.timestamp
                                  ? new Date(comment.timestamp)
                                      .toLocaleString('en-GB', { timeZone: 'Asia/Singapore', hour12: false })
                                      .replace(',', '')
                                  : ''}{' '}
                                {comment.operator}
                              </span>
                            </div>
                            <div key={`comment-message-${comment.id}`} className="text-sm">
                              {comment.message}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            </td>
            <td className="m-4" style={{ width: '50%' }}>
              <Card>
                <CardContent className="p-2">
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      style={{ width: '100%' }}
                    />
                  </Form.Group>
                  <div className="mt-2 flex justify-end">
                    <Button variant="primary" onClick={handleAddComment} disabled={!newComment.trim() || !user}>
                      Add Comment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Fulfillment Tracking - Deleo */}
      {!order.parxlData && (
        <Card>
          <CardHeader className="py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Fulfillment Tracking</CardTitle>
                <span className="badge bg-primary text-white ms-2">DELEO</span>
              </div>
              {order.deleoData?.deleoOrderId && onResetDeleoOrder && (
                <Button variant="outline-warning" size="sm" onClick={() => setShowResetDeleoModal(true)}>
                  Reset Order
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent>
            <Table className="mb-0">
              <thead>
                <tr>
                  <th className="text-sm text-muted-foreground" colSpan={7}>
                    <div className="font-medium mt-2 mb-2">SHIPMENT DETAILS</div>
                  </th>
                </tr>
                <tr>
                  <th className="text-sm text-muted-foreground border">Provider Order ID</th>
                  <th className="text-sm text-muted-foreground border">Tracking No</th>
                  <th className="text-sm text-muted-foreground border">Total Amount</th>
                  <th className="text-sm text-muted-foreground border">Status Code</th>
                  <th className="text-sm text-muted-foreground border">Status Description</th>
                  <th className="text-sm text-muted-foreground border">Status KR</th>
                </tr>
              </thead>
              <tbody>
                {!order.deleoData || !order.deleoData.deleoOrderId ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-sm text-muted-foreground flex items-center gap-2 border"
                      style={{ color: 'red' }}
                    >
                      <AlertTriangle className="h-4 w-4 m-2" />
                      No fulfillment order created! You can retry to create.
                      <Button variant="outline-danger" size="sm" onClick={onRetryDeleoOrder} className="m-2">
                        Create Order
                      </Button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                      {order.deleoData.deleoOrderId}
                    </td>
                    <td className="text-sm font-medium border">{order.deleoData.deleoTrackingNo}</td>
                    <td className="text-sm font-medium border">
                      {order.currency} {getDeleoTotalAmount().toFixed(2)}
                      {Math.abs(getDeleoTotalAmount() - getWooLineItemsTotalAmount()) > 1 && (
                        <small className="m-2" style={{ color: 'red' }}>
                          <AlertTriangle className="h-1 w-1 inline-block" />
                          Amount mismatch! (Provider: {getDeleoTotalAmount().toFixed(2)} - Woo:{' '}
                          {getWooLineItemsTotalAmount().toFixed(2)}) ={' '}
                          {Math.abs(getDeleoTotalAmount() - getWooLineItemsTotalAmount()).toFixed(2)}
                        </small>
                      )}
                    </td>
                    <td className="text-sm font-medium border">
                      {order.deleoData.deleoStatus?.code} {order.deleoData.deleoStatus?.name}
                    </td>
                    <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                      {getStatusText(order.deleoData.deleoStatus?.name || '')}
                    </td>
                    <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                      {getStatusDescription(order.deleoData.deleoStatus?.name || '')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

          <Table className="mt-2">
            <thead>
              <tr>
                <th className="text-sm text-muted-foreground" colSpan={8}>
                  <div className="font-medium mt-4 mb-2">ORDER ITEMS</div>
                </th>
              </tr>
              <tr>
                <th className="text-sm text-muted-foreground border">Order Item ID</th>
                <th className="text-sm text-muted-foreground border">Item Name</th>
                <th className="text-sm text-muted-foreground border">Item Code</th>
                <th className="text-sm text-muted-foreground border">Quantity</th>
                <th className="text-sm text-muted-foreground border">Price</th>
                <th className="text-sm text-muted-foreground border">Status</th>
                <th className="text-sm text-muted-foreground border">Status KR</th>
              </tr>
            </thead>
            <tbody>
              {!order.deleoData?.orderItemList || order.deleoData.orderItemList.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-sm text-muted-foreground flex items-center gap-2 border"
                    style={{ color: 'orange' }}
                  >
                    <Info className="h-4 w-4 m-2" />
                    No order item records found
                  </td>
                </tr>
              ) : (
                order.deleoData.orderItemList.map((item, index) => (
                  <tr key={index} className="table-bordered">
                    <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                      {item.orderItemId}
                    </td>
                    <td className="text-sm font-medium border">{item.itemNm}</td>
                    <td className="text-sm font-medium border">{item.orgItemCd}</td>
                    <td className="text-sm font-medium border">{item.quantity}</td>
                    <td className="text-sm font-medium border">
                      {order.currency} {item.amt}
                    </td>
                    <td className="text-sm font-medium border">{item.status?.name}</td>
                    <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                      {item.status?.desc}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Table className="mt-2">
            <thead>
              <tr>
                <th className="text-sm text-muted-foreground" colSpan={11}>
                  <div className="font-medium mt-4 mb-2">CARRIER TRACKING</div>
                </th>
              </tr>
              <tr>
                <th className="text-sm text-muted-foreground border">Carrier</th>
                <th className="text-sm text-muted-foreground border">Carrier Tracking No</th>
                <th className="text-sm text-muted-foreground border">SS Request ID</th>
                <th className="text-sm text-muted-foreground border">Created On</th>
                <th className="text-sm text-muted-foreground border">Updated On</th>
                <th className="text-sm text-muted-foreground border">WMS ASN Code</th>
                <th className="text-sm text-muted-foreground border">Provider Tracking No</th>
              </tr>
            </thead>
            <tbody>
              {!order.deleoData?.orderTrackingList || order.deleoData?.orderTrackingList?.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="text-sm text-muted-foreground flex items-center gap-2 border"
                    style={{ color: 'orange' }}
                  >
                    <Info className="h-4 w-4 m-2" />
                    Package hasn't been handed over to a carrier yet
                  </td>
                </tr>
              ) : (
                order.deleoData?.orderTrackingList?.map((tracking, index) => (
                  <tr key={index}>
                    <td className="text-sm font-medium border">{tracking.deliveryCompanyCd.name}</td>
                    <td className="text-sm font-medium border">{tracking.trackingNo}</td>
                    <td className="text-sm font-medium border">{tracking.requestId}</td>
                    <td className="text-sm font-medium border">{tracking.createdOn}</td>
                    <td className="text-sm font-medium border">{tracking.updatedOn}</td>
                    <td className="text-sm font-medium border">{tracking.wmsAsnCd}</td>
                    <td className="text-sm font-medium border">
                      {tracking.deleoTrackingNo ? (
                        <Button
                          onClick={() =>
                            navigate(ROUTES.oms.deliveryTrace(order.orderId, tracking.deleoTrackingNo || ''))
                          }
                          variant="outline-primary"
                          size="sm"
                        >
                          {tracking.deleoTrackingNo}
                        </Button>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Fulfillment Tracking - Parxl */}
      {order.parxlData && (
        <Card>
          <CardHeader className="py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Fulfillment Tracking</CardTitle>
                <span className="badge bg-info text-white ms-2">PARXL</span>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <Table className="mb-0">
              <thead>
                <tr>
                  <th className="text-sm text-muted-foreground" colSpan={7}>
                    <div className="font-medium mt-2 mb-2">SHIPMENT DETAILS</div>
                  </th>
                </tr>
                <tr>
                  <th className="text-sm text-muted-foreground border">PLS Shipment Reference</th>
                  <th className="text-sm text-muted-foreground border">Merchant Order No</th>
                  <th className="text-sm text-muted-foreground border">Order Date</th>
                  <th className="text-sm text-muted-foreground border">Service Option</th>
                  <th className="text-sm text-muted-foreground border">Latest Status</th>
                  <th className="text-sm text-muted-foreground border">Incoterm</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-sm font-medium border" style={{ color: 'blue' }}>
                    {order.parxlData.plsShipmentReference}
                  </td>
                  <td className="text-sm font-medium border">{order.parxlData.merchantOrderNo}</td>
                  <td className="text-sm font-medium border">{order.parxlData.orderDate}</td>
                  <td className="text-sm font-medium border">{order.parxlData.serviceOption}</td>
                  <td className="text-sm font-medium border">
                    <span className={`badge ${getShippingStatusBadgeClass(order.shippingData?.status)}`}>
                      {order.parxlData.latestTrackingStatus}
                    </span>
                  </td>
                  <td className="text-sm font-medium border">{order.parxlData.incoterm || 'DDU'}</td>
                </tr>
              </tbody>
            </Table>

            <Table className="mt-2">
              <thead>
                <tr>
                  <th className="text-sm text-muted-foreground" colSpan={4}>
                    <div className="font-medium mt-4 mb-2">TRACKING HISTORY</div>
                  </th>
                </tr>
                <tr>
                  <th className="text-sm text-muted-foreground border">Date</th>
                  <th className="text-sm text-muted-foreground border">Status</th>
                  <th className="text-sm text-muted-foreground border">Description</th>
                  <th className="text-sm text-muted-foreground border">Location</th>
                </tr>
              </thead>
              <tbody>
                {!order.parxlData.trackingStatus || order.parxlData.trackingStatus.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-sm text-muted-foreground flex items-center gap-2 border"
                      style={{ color: 'orange' }}
                    >
                      <Info className="h-4 w-4 m-2" />
                      No tracking events yet
                    </td>
                  </tr>
                ) : (
                  order.parxlData.trackingStatus.map((event, index) => (
                    <tr key={index}>
                      <td className="text-sm font-medium border">{event.date}</td>
                      <td className="text-sm font-medium border">
                        <span className="badge bg-secondary">{event.status}</span>
                      </td>
                      <td className="text-sm font-medium border">{event.description || '-'}</td>
                      <td className="text-sm font-medium border">{event.location || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {order.shippingData?.labelUrl && (
              <div className="mt-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => window.open(order.shippingData?.labelUrl, '_blank')}
                >
                  <Printer className="h-4 w-4 me-2" />
                  Download Label
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Line Items Card */}
      <Card style={{ width: '100%' }} className="border-top mt-5">
        <CardHeader className="py-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Line Items</CardTitle>
            <CardDescription>
              Click the Edit button in the Actions column to modify a line item. Select items on the checkboxes to
              include in inbound requests. Only checkboxed items will be included in the request.
              <br /> * SKU, Names, and Quantity are from WooCommerce orders originally before recalculation with
              Variation packs.
              <br /> <Bot className="h-4 w-4 inline-block" /> fields are auto-populated by the Agent.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="mb-3 p-2 bg-light rounded">
            <strong className="me-2">Total Quantity:</strong>
            <span className="text-dark fw-bold fs-5">
              {order.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
            </span>
          </div>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th className="py-2 px-3 text-center border bg-light text-success" colSpan={1}>
                  Action
                </th>
                <th className="py-2 px-3 text-center border bg-light text-success" colSpan={8}>
                  Product Information (WooCommerce)
                </th>
                <th className="py-2 px-3 text-center border bg-light text-success" colSpan={2}>
                  Inventory
                </th>
                <th className="py-2 px-3 text-center border bg-light text-success" colSpan={8}>
                  Sourcing Shipment Track by Agent
                </th>
                <th className="py-2 px-3 text-center border bg-light text-success" colSpan={2}>
                  Ops Actions
                </th>
              </tr>
              <tr>
                <th className="py-2 px-3 text-left border">
                  Inbound Request
                  <Form.Check
                    type="checkbox"
                    checked={
                      selectedItems.size > 0 &&
                      selectedItems.size === order.lineItems.filter((item) => item.cpOrderNumber).length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="py-2 px-3 text-left border">SKU</th>
                <th className="py-2 px-3 text-left border">Image</th>
                <th className="py-2 px-3 text-left border">Product Name</th>
                <th className="py-2 px-3 text-left border">Product Name KR</th>
                <th className="py-2 px-3 text-left border">Size</th>
                <th className="py-2 px-3 text-right border">Quantity</th>
                <th className="py-2 px-3 text-right border">Subtotal</th>
                <th className="py-2 px-3 text-right border">Total</th>
                <th className="py-2 px-3 text-right border">Latest Inventory Count</th>
                <th className="py-2 px-3 text-center border">Inbound Status</th>
                <th className="py-2 px-3 text-left border">Sourcing Route</th>
                <th className="py-2 px-3 text-left border">Order Number</th>
                <th className="py-2 px-3 text-left border">
                  Order Date <Bot className="h-4 w-4 inline-block" />
                </th>
                <th className="py-2 px-3 text-left border">
                  Product Name <Bot className="h-4 w-4 inline-block" />
                </th>
                <th className="py-2 px-3 text-right border">
                  Product Cost <Bot className="h-4 w-4 inline-block" />
                </th>
                <th className="py-2 px-3 text-left border">
                  Tracking Number <Bot className="h-4 w-4 inline-block" />
                </th>
                <th className="py-2 px-3 text-left border">
                  Delivery Company <Bot className="h-4 w-4 inline-block" />
                </th>
                <th className="py-2 px-3 text-left border">CP Link</th>
                <th className="py-2 px-3 text-left border">Comments</th>
                <th className="py-2 px-3 text-right border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {order.lineItems.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="py-2 px-3 border">
                    <Form.Check
                      type="checkbox"
                      checked={selectedItems.has(index)}
                      onChange={() => handleCheckboxChange(index)}
                      disabled={!item.cpOrderNumber}
                      title={!item.cpOrderNumber ? 'CP Order Number is required' : ''}
                    />
                  </td>
                  <td className="py-2 px-3 text-sm font-medium border">
                    <div>
                      <a
                        href={`${getAdminBaseUrl()}/catalog/ss-editor?sku=${item.sku}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {item.sku}
                      </a>
                    </div>
                    {item.originalSku && <div style={{ color: '#6b7280' }}>({item.originalSku}*)</div>}
                  </td>
                  <td className="py-2 px-3 border">
                    {productImages[item.sku] ? (
                      <a
                        href={productImages[item.sku]}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Click to view full size image"
                      >
                        <img
                          src={productImages[item.sku]}
                          alt={item.name}
                          style={{ width: '150px', height: '150px' }}
                          className="object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">No image</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border" style={{ minWidth: '300px' }}>
                    <div>{item.name}</div>
                    {item.originalName && (
                      <div className="text-xs mt-1" style={{ color: '#6b7280' }}>
                        ({item.originalName}*)
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border" style={{ minWidth: '300px' }}>
                    <div>{productNamesKor[item.sku]}</div>
                    {item.originalNameKor && (
                      <div className="text-xs mt-1" style={{ color: '#6b7280' }}>
                        ({item.originalNameKor}*)
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editingItem.item.productSize || ''}
                        onChange={(e) => handleEditChange('productSize', e.target.value)}
                        style={{ minWidth: '80px' }}
                        placeholder="Size"
                      />
                    ) : (
                      item.productSize || ''
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-right border">
                    <div>
                      {editingItem?.index === index ? (
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={editingItem.item.quantity}
                          min={1}
                          max={100}
                          onChange={(e) => handleEditChange('quantity', parseInt(e.target.value))}
                        />
                      ) : (
                        item.quantity
                      )}
                    </div>
                    {item.originalQuantity && (
                      <div className="text-xs" style={{ color: '#6b7280' }}>
                        ({item.originalQuantity}*)
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-right border">
                    {editingItem?.index === index ? (
                      <MoneyInput
                        value={editingItem.item.subtotal ?? null}
                        onChange={(value) => handleEditChange('subtotal', value)}
                        className="form-control"
                      />
                    ) : (
                      <MoneyDisplay currency={order.currency} value={item.subtotal} />
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-right border">
                    {editingItem?.index === index ? (
                      <MoneyInput
                        value={editingItem.item.total ?? null}
                        onChange={(value) => handleEditChange('total', value)}
                        className="form-control"
                      />
                    ) : (
                      <MoneyDisplay currency={order.currency} value={item.total} />
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-right border">
                    {stockStatusBySku[item.sku] !== undefined ? (
                      <span>{stockStatusBySku[item.sku] || 0}</span>
                    ) : (
                      <span className="text-gray-500">checking...</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-center border">
                    <span className={`badge ${getInboundStatusBadgeClass(item.inboundStatus)}`}>
                      {getInboundStatusText(item.inboundStatus)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Select
                        size="sm"
                        value={
                          editingItem.item.sourcingRoute ||
                          (canUseExistingInventory(editingItem.item.sku, editingItem.item.quantity)
                            ? 'Existing inventory'
                            : '')
                        }
                        onChange={(e) => handleEditChange('sourcingRoute', e.target.value)}
                        style={{ minWidth: '160px' }}
                      >
                        <option value="">Select</option>
                        {SOURCING_ROUTES.map((route) => (
                          <option key={route.value} value={route.value}>
                            {route.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div>
                        {item.sourcingRoute || ''}
                        {shouldShowUseInventoryButton(item) && (
                          <div className="mt-1">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleUseExistingInventory(item)}
                              disabled={loadingStockStatus}
                              title={`Use ${getRemainingQuantity(item.sku)} available units from existing inventory`}
                            >
                              ✓ Use existing inventory ({getRemainingQuantity(item.sku)})
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editingItem.item.sourcingShipmentTrack?.orderNumber || ''}
                        onChange={(e) => {
                          const updatedItem = {
                            ...editingItem.item,
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: e.target.value,
                              orderDate:
                                editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || '',
                              trackingNumber:
                                editingItem.item.sourcingShipmentTrack?.trackingNumber ||
                                editingItem.item.cpOrderNumber ||
                                '',
                              deliveryCompanyCd:
                                editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                                editingItem.item.deliveryCompanyCd ||
                                '',
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        style={{ minWidth: '150px' }}
                      />
                    ) : item.sourcingRoute === 'Coupang' && item.sourcingShipmentTrack?.orderNumber ? (
                      <a
                        href={`https://mc.coupang.com/ssr/desktop/order/${item.sourcingShipmentTrack.orderNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {item.sourcingShipmentTrack.orderNumber}
                      </a>
                    ) : (
                      item.sourcingShipmentTrack?.orderNumber || ''
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || ''}
                        onChange={(e) => {
                          const updatedItem = {
                            ...editingItem.item,
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: editingItem.item.sourcingShipmentTrack?.orderNumber || '',
                              orderDate: e.target.value,
                              trackingNumber:
                                editingItem.item.sourcingShipmentTrack?.trackingNumber ||
                                editingItem.item.cpOrderNumber ||
                                '',
                              deliveryCompanyCd:
                                editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                                editingItem.item.deliveryCompanyCd ||
                                '',
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        style={{ minWidth: '150px' }}
                        placeholder="YYYY-MM-DD"
                        maxLength={10}
                      />
                    ) : (
                      item.sourcingShipmentTrack?.orderDate || item.cpOrderDate || ''
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editingItem.item.sourcingShipmentTrack?.productName || ''}
                        onChange={(e) => {
                          const updatedItem = {
                            ...editingItem.item,
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: editingItem.item.sourcingShipmentTrack?.orderNumber || '',
                              orderDate:
                                editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || '',
                              productName: e.target.value,
                              trackingNumber:
                                editingItem.item.sourcingShipmentTrack?.trackingNumber ||
                                editingItem.item.cpOrderNumber ||
                                '',
                              deliveryCompanyCd:
                                editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                                editingItem.item.deliveryCompanyCd ||
                                '',
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        style={{ minWidth: '200px' }}
                      />
                    ) : (
                      <span style={{ fontSize: '0.8rem' }}>{item.sourcingShipmentTrack?.productName || ''}</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm text-right border">
                    {editingItem?.index === index ? (
                      <MoneyInput
                        value={
                          editingItem.item.sourcingShipmentTrack?.productCost ?? editingItem.item.cpProductCost ?? null
                        }
                        onChange={(value) => {
                          const updatedItem = {
                            ...editingItem.item,
                            cpProductCost: Number(value),
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: editingItem.item.sourcingShipmentTrack?.orderNumber || '',
                              orderDate:
                                editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || '',
                              trackingNumber:
                                editingItem.item.sourcingShipmentTrack?.trackingNumber ||
                                editingItem.item.cpOrderNumber ||
                                '',
                              deliveryCompanyCd:
                                editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                                editingItem.item.deliveryCompanyCd ||
                                '',
                              productCost: Number(value),
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        className="form-control"
                      />
                    ) : (item.sourcingShipmentTrack?.productCost ?? item.cpProductCost) ? (
                      <MoneyDisplay
                        value={item.sourcingShipmentTrack?.productCost ?? item.cpProductCost}
                        currency={'KRW'}
                      />
                    ) : null}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={
                          editingItem.item.sourcingShipmentTrack?.trackingNumber || editingItem.item.cpOrderNumber || ''
                        }
                        onChange={(e) => {
                          const updatedItem = {
                            ...editingItem.item,
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: editingItem.item.sourcingShipmentTrack?.orderNumber || '',
                              orderDate:
                                editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || '',
                              trackingNumber: e.target.value,
                              deliveryCompanyCd:
                                editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                                editingItem.item.deliveryCompanyCd ||
                                '',
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        style={{ minWidth: '150px' }}
                      />
                    ) : item.sourcingShipmentTrack?.shipmentTrackUrl ? (
                      <a
                        href={item.sourcingShipmentTrack.shipmentTrackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {item.sourcingShipmentTrack?.trackingNumber || 'Preparing'}
                      </a>
                    ) : item.sourcingShipmentTrack?.trackingNumber ? (
                      item.sourcingShipmentTrack.trackingNumber
                    ) : (
                      item.cpOrderNumber
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Select
                        size="sm"
                        value={
                          editingItem.item.sourcingShipmentTrack?.deliveryCompanyCd ||
                          editingItem.item.deliveryCompanyCd ||
                          ''
                        }
                        onChange={(e) => {
                          const updatedItem = {
                            ...editingItem.item,
                            deliveryCompanyCd: e.target.value,
                            sourcingShipmentTrack: {
                              ...editingItem.item.sourcingShipmentTrack,
                              orderNumber: editingItem.item.sourcingShipmentTrack?.orderNumber || '',
                              orderDate:
                                editingItem.item.sourcingShipmentTrack?.orderDate || editingItem.item.cpOrderDate || '',
                              trackingNumber:
                                editingItem.item.sourcingShipmentTrack?.trackingNumber ||
                                editingItem.item.cpOrderNumber ||
                                '',
                              deliveryCompanyCd: e.target.value,
                            } as any,
                          };
                          setEditingItem({
                            ...editingItem,
                            item: updatedItem,
                          });
                        }}
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">Select</option>
                        {DELIVERY_COMPANIES.map((company) => (
                          <option key={company.code} value={company.code}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      getDeliveryCompanyName(
                        item.sourcingShipmentTrack?.deliveryCompanyCd || item.deliveryCompanyCd || '',
                      )
                    )}
                  </td>
                  <td className="py-2 px-3 text-sm border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        size="sm"
                        value={editingItem.item.cpUrl || ''}
                        onChange={(e) => handleEditChange('cpUrl', e.target.value)}
                        style={{ minWidth: '160px' }}
                      />
                    ) : item.cpUrl ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={item.cpUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {item.cpUrl.length > 40 ? `${item.cpUrl.substring(0, 40)}...` : item.cpUrl}
                        </a>
                      </div>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="py-2 px-3 border">
                    {editingItem?.index === index ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={editingItem.item.comments || ''}
                        onChange={(e) => handleEditChange('comments', e.target.value)}
                        style={{ minWidth: '200px' }}
                      />
                    ) : (
                      item.comments || ''
                    )}
                  </td>
                  <td className="py-2 px-3 border">
                    <div className="flex items-center justify-end gap-2">
                      {editingItem?.index === index ? (
                        <>
                          <Button variant="outline-success" size="sm" onClick={handleSaveEdit}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={handleCancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="outline-primary" size="sm" onClick={() => handleEditItem(item, index)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteItem(item)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {isAddingLineItem && (
                <tr className="border-t bg-muted">
                  <td className="py-2 px-3 border">
                    <Form.Check type="checkbox" disabled title="Cannot select new items for inbound request" />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="SKU"
                      value={newLineItem.sku || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, sku: e.target.value })}
                      style={{ minWidth: '100px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border"></td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Product Name"
                      value={newLineItem.name || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, name: e.target.value })}
                      style={{ minWidth: '300px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Product Name KR"
                      value={newLineItem.nameKor || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, nameKor: e.target.value })}
                      style={{ minWidth: '300px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Size"
                      value={newLineItem.productSize || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, productSize: e.target.value })}
                      style={{ minWidth: '80px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="number"
                      size="sm"
                      min={1}
                      max={100}
                      placeholder="Quantity"
                      value={newLineItem.quantity || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, quantity: parseInt(e.target.value) })}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <MoneyInput
                      value={newLineItem.price ?? null}
                      onChange={(value) =>
                        setNewLineItem({
                          ...newLineItem,
                          price: Number(value),
                        })
                      }
                      minWidth="100px"
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <MoneyInput
                      value={newLineItem.total ?? null}
                      onChange={(value) =>
                        setNewLineItem({
                          ...newLineItem,
                          total: Number(value),
                        })
                      }
                      minWidth="100px"
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    {stockStatusBySku[newLineItem.sku ?? ''] !== undefined ? (
                      <span>{stockStatusBySku[newLineItem.sku ?? ''] || 0}</span>
                    ) : (
                      <span className="text-gray-500">checking...</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center border">
                    <span className="badge bg-secondary">PENDING</span>
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Select
                      size="sm"
                      value={newLineItem.sourcingRoute || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, sourcingRoute: e.target.value })}
                      style={{ minWidth: '160px' }}
                    >
                      <option value="">Select</option>
                      {SOURCING_ROUTES.map((route) => (
                        <option key={route.value} value={route.value}>
                          {route.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={newLineItem.sourcingShipmentTrack?.orderNumber || ''}
                      onChange={(e) =>
                        setNewLineItem({
                          ...newLineItem,
                          sourcingShipmentTrack: {
                            orderNumber: e.target.value,
                            orderDate: newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || '',
                            trackingNumber:
                              newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || '',
                            deliveryCompanyCd:
                              newLineItem.sourcingShipmentTrack?.deliveryCompanyCd ||
                              newLineItem.deliveryCompanyCd ||
                              '',
                          } as any,
                        })
                      }
                      style={{ minWidth: '150px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="date"
                      size="sm"
                      value={newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || ''}
                      onChange={(e) =>
                        setNewLineItem({
                          ...newLineItem,
                          cpOrderDate: e.target.value,
                          sourcingShipmentTrack: {
                            orderNumber: newLineItem.sourcingShipmentTrack?.orderNumber || '',
                            orderDate: e.target.value,
                            trackingNumber:
                              newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || '',
                            deliveryCompanyCd:
                              newLineItem.sourcingShipmentTrack?.deliveryCompanyCd ||
                              newLineItem.deliveryCompanyCd ||
                              '',
                          } as any,
                        })
                      }
                      style={{ minWidth: '150px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={newLineItem.sourcingShipmentTrack?.productName || ''}
                      onChange={(e) =>
                        setNewLineItem({
                          ...newLineItem,
                          sourcingShipmentTrack: {
                            orderNumber: newLineItem.sourcingShipmentTrack?.orderNumber || '',
                            orderDate: newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || '',
                            productName: e.target.value,
                            trackingNumber:
                              newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || '',
                            deliveryCompanyCd:
                              newLineItem.sourcingShipmentTrack?.deliveryCompanyCd ||
                              newLineItem.deliveryCompanyCd ||
                              '',
                          } as any,
                        })
                      }
                      style={{ minWidth: '200px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <MoneyInput
                      value={newLineItem.sourcingShipmentTrack?.productCost ?? newLineItem.cpProductCost ?? null}
                      onChange={(value) =>
                        setNewLineItem({
                          ...newLineItem,
                          cpProductCost: Number(value),
                          sourcingShipmentTrack: {
                            orderNumber: newLineItem.sourcingShipmentTrack?.orderNumber || '',
                            orderDate: newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || '',
                            trackingNumber:
                              newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || '',
                            deliveryCompanyCd:
                              newLineItem.sourcingShipmentTrack?.deliveryCompanyCd ||
                              newLineItem.deliveryCompanyCd ||
                              '',
                            productCost: Number(value),
                          } as any,
                        })
                      }
                      minWidth="120px"
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || ''}
                      onChange={(e) =>
                        setNewLineItem({
                          ...newLineItem,
                          cpOrderNumber: e.target.value,
                          sourcingShipmentTrack: {
                            orderNumber: newLineItem.sourcingShipmentTrack?.orderNumber || '',
                            orderDate: newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || '',
                            trackingNumber: e.target.value,
                            deliveryCompanyCd:
                              newLineItem.sourcingShipmentTrack?.deliveryCompanyCd ||
                              newLineItem.deliveryCompanyCd ||
                              '',
                          } as any,
                        })
                      }
                      style={{ minWidth: '150px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Select
                      size="sm"
                      value={
                        newLineItem.sourcingShipmentTrack?.deliveryCompanyCd || newLineItem.deliveryCompanyCd || ''
                      }
                      onChange={(e) =>
                        setNewLineItem({
                          ...newLineItem,
                          deliveryCompanyCd: e.target.value,
                          sourcingShipmentTrack: {
                            orderNumber: newLineItem.sourcingShipmentTrack?.orderNumber || '',
                            orderDate: newLineItem.sourcingShipmentTrack?.orderDate || newLineItem.cpOrderDate || '',
                            trackingNumber:
                              newLineItem.sourcingShipmentTrack?.trackingNumber || newLineItem.cpOrderNumber || '',
                            deliveryCompanyCd: e.target.value,
                          } as any,
                        })
                      }
                      style={{ minWidth: '150px' }}
                    >
                      <option value="">Select</option>
                      {DELIVERY_COMPANIES.map((company) => (
                        <option key={company.code} value={company.code}>
                          {company.name}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={newLineItem.cpUrl || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, cpUrl: e.target.value })}
                      style={{ minWidth: '160px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={newLineItem.comments || ''}
                      onChange={(e) => setNewLineItem({ ...newLineItem, comments: e.target.value })}
                      style={{ minWidth: '200px' }}
                    />
                  </td>
                  <td className="py-2 px-3 border">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleAddLineItem}
                        disabled={!newLineItem.sku || !newLineItem.quantity}
                      >
                        Add
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setIsAddingLineItem(false);
                          setNewLineItem({});
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between p-2">
            <div className="d-flex align-items-center gap-3">
              <div className="text-muted">
                <strong>Total Quantity:</strong>{' '}
                <span className="text-dark fw-bold">
                  {order.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                </span>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handleRequestInbound}
                  disabled={selectedItems.size === 0 || !order.deleoData?.deleoOrderId}
                  className="flex items-center gap-2"
                  title={selectedItems.size === 0 ? 'Select items with CP Order Number to request inbound' : ''}
                >
                  <Truck className="h-4 w-4" />
                  Request Inbound
                </Button>
                <Button
                  variant="outline-success"
                  onClick={handleOutboundToDeleo}
                  disabled={!canOutboundToDeleo() || processingOutbound}
                  className="flex items-center gap-2"
                  title={!canOutboundToDeleo() ? 'All line items must be INBOUNDED to proceed with outbound' : ''}
                >
                  <Package className="h-4 w-4" />
                  {processingOutbound ? 'Processing...' : 'Outbound to Deleo BaroShip'}
                </Button>
              </div>
            </div>
            {!isAddingLineItem && (
              <Button
                variant="outline-primary"
                onClick={() => setIsAddingLineItem(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Line Item
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Modal show={Boolean(itemToDelete)} onHide={() => setItemToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This action cannot be undone. This will permanently delete the line item.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setItemToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Inbound Confirmation Modal */}
      <Modal show={showInboundConfirm} onHide={() => setShowInboundConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Inbound Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The following items will be included in the inbound request:</p>
          <div className="space-y-4 mt-3">
            {trackingList.map((group) => (
              <div key={`tracking-group-${group.trackingNo}`} className="border rounded p-3">
                <div className="font-medium mb-2">
                  Tracking Number: {group.trackingNo === 'unknown' ? 'Not Specified' : group.trackingNo}
                </div>
                <div className="text-sm text-muted-foreground mb-2">
                  Delivery Company:{' '}
                  {group.deliveryCompanyCode
                    ? DELIVERY_COMPANIES.find((c) => c.code === group.deliveryCompanyCode)?.name ||
                      group.deliveryCompanyCode
                    : 'Not Specified'}
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  {group.itemList.map((item) => {
                    const lineItem = order.lineItems.find(
                      (li) => li.sku === item.code && li.quantity === item.quantity,
                    );
                    return (
                      <li key={`item-${item.code}-${item.quantity}`} className="text-sm">
                        {item.code} - {lineItem?.name} (Qty: {item.quantity})
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInboundConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmInbound}>
            Confirm Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Inbound Confirmation Modal */}
      <Modal show={Boolean(inboundToCancel)} onHide={() => setInboundToCancel(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Inbound Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel this inbound request?</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setInboundToCancel(null)}>
            No, Keep It
          </Button>
          <Button variant="danger" onClick={handleConfirmCancelInbound}>
            Yes, Cancel Inbound
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Deleo Order Confirmation Modal */}
      <Modal show={showResetDeleoModal} onHide={() => setShowResetDeleoModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Deleo Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning" role="alert">
            <AlertTriangle className="h-5 w-5 mb-2" />
            <p className="mb-2">
              <strong>Warning:</strong> This action will cancel the existing Deleo order and create a new one.
            </p>
            <p className="mb-0 text-danger">
              <strong>This action cannot be undone.</strong>
            </p>
          </div>
          <p>Are you sure you want to reset the Deleo order?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetDeleoModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={async () => {
              if (onResetDeleoOrder) {
                await onResetDeleoOrder();
              }
              setShowResetDeleoModal(false);
            }}
          >
            Reset Deleo Order
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 숨겨진 프린트용 컴포넌트 */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }}>
        <OrderPdfPreview ref={printRef} order={order} />
      </div>

      <Modal show={showPdfPreview} onHide={() => setShowPdfPreview(false)} dialogClassName="modal-pdf-preview">
        <style>{`
          .modal-pdf-preview .modal-dialog {
            max-width: 780px;
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>PDF Preview - Order {order.orderId}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <OrderPdfPreview order={order} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPdfPreview(false)}>
            Close
          </Button>
          <Button variant="outline-primary" onClick={handlePrint}>
            <Printer className="h-4 w-4 me-1" />
            Print
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
